import type { ReactNode } from 'react';

/**
 * Form primitives for settings screens.
 *
 * A settings page is mostly label/control/explanation triples, and hand-rolling
 * that markup per field is where inconsistent spacing and missing `for`
 * attributes come from. These carry the label association and the hint slot so
 * neither can be forgotten.
 */

export function Section({ title, description, children, columns = 2 }: {
  title: string; description?: string; children: ReactNode; columns?: 1 | 2 | 3;
}) {
  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head">
        <div className="arc-stack">
          <h2 className="arc-h2">{title}</h2>
          {description && (
            <span className="arc-subtle" style={{ fontSize: 11.5 }}>{description}</span>
          )}
        </div>
      </div>
      <div className="arc-card-body">
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: '14px 20px',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, hint, error, htmlFor, span, children }: {
  label: string; hint?: string; error?: string; htmlFor?: string;
  span?: boolean; children: ReactNode;
}) {
  return (
    <div className="arc-field" style={{ margin: 0, ...(span ? { gridColumn: '1 / -1' } : {}) }}>
      <label className="arc-label" htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <div className="arc-error">{error}</div>
        : hint ? <div className="arc-hint">{hint}</div> : null}
    </div>
  );
}

/**
 * A labelled checkbox with its explanation attached.
 *
 * Settings toggles are where a UI most often loses its "why" — the label says
 * what it does, the hint says what happens if you turn it on.
 */
export function Toggle({ id, label, hint, checked, onChange, disabled }: {
  id: string; label: string; hint?: string;
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <div className="arc-field" style={{ margin: 0 }}>
      <div className="arc-row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <input
          id={id} type="checkbox" checked={checked} disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <div className="arc-stack">
          <label htmlFor={id} style={{ fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}>
            {label}
          </label>
          {hint && <span className="arc-hint" style={{ marginTop: 0 }}>{hint}</span>}
        </div>
      </div>
    </div>
  );
}

/** Sticky footer so Save is reachable without scrolling a long settings form. */
export function FormActions({ dirty, saving, onSave, onReset, error }: {
  dirty: boolean; saving: boolean; onSave: () => void; onReset: () => void; error?: string | null;
}) {
  return (
    <div
      style={{
        position: 'sticky', bottom: 0, zIndex: 5,
        background: 'var(--arc-surface)', borderTop: '1px solid var(--arc-border)',
        padding: '10px 14px', margin: '0 -20px -56px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}
    >
      {error && <span className="arc-error" style={{ margin: 0 }}>{error}</span>}
      <div className="arc-spacer" />
      {dirty && (
        <span className="arc-subtle" style={{ fontSize: 12 }}>Unsaved changes</span>
      )}
      <button type="button" className="arc-btn" onClick={onReset} disabled={!dirty || saving}>
        Discard
      </button>
      <button type="button" className="arc-btn arc-btn-primary" onClick={onSave}
        disabled={!dirty || saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}
